// lib/leasing/sourcingMandate.ts
//
// THE LICENCE A DRIVING AGENT RUNS ON. The Source agent drives sourcing for the user: it reads the
// asset, sets the parameters, launches, reads the shortlist, then refines, extends or relaunches
// until the list is coherent (owner, 2026-09-28). Every billed step after the first happens in a
// turn nobody typed (the run landing wakes it), and the launch guard rightly refuses those
// (lib/leasing/unprompted-turns.ts). The mandate is how the user authorises that work ONCE: an
// asset, a goal and a credit ceiling, set in a turn where they said go. Within it the agent may
// commit spend on its own; past the ceiling, past the expiry, on another asset or after a stop,
// the usual guards apply again.
//
// The ceiling counts COMMITTED budgets (each launch or extension's own budget), not what the runs
// end up spending: a run's budget is its maximum, so the mandate can never be overrun, only
// under-used.
import type { ServerSourcingMandate, SourcingMandate } from '#types/types';
import { brandsForCredits } from '#lib/leasing/sourceBudget';

/** The model that drives Source (app/api/ai/assist/route.ts pins commander turns to it). */
export const SOURCE_AGENT_MODEL = 'gpt-6-astra';

export const MANDATE_MAX_CEILING_CREDITS = 3000;
// No cap (owner, 2026-10-12: 'the agent should always use as many credits as necessary to attain the
// score'): a ceiling no run reaches, so every sum below stays true. Such a mandate still ends on the
// champion, an exhausted candidate pool or MANDATE_TTL_MS, and spends only what the organisation holds.
export const MANDATE_UNCAPPED_CREDITS = 1_000_000;
export const isUncapped = (creditCeiling: number) => creditCeiling >= MANDATE_UNCAPPED_CREDITS;
// The widest step an uncapped mandate takes in one pass: the ceiling no longer bounds it.
const UNCAPPED_STEP_BRANDS = 100;
export const MANDATE_TTL_MS = 6 * 60 * 60 * 1000;
/** The score a champion must reach before the agent may stop on its own (owner, 2026-09-28). */
export const MANDATE_DEFAULT_TARGET_SCORE = 90;
export const MANDATE_MIN_TARGET_SCORE = 50;

export const createMandate = (params: {
  goal: string;
  propertyIds: string[];
  /** null: no cap, the mandate spends what reaching the target takes. */
  creditCeiling: number | null;
  origin?: SourcingMandate['origin'];
  /** A unit fit: the one unit of the asset. */
  unitId?: string | null;
  /** The champion's score to reach; MANDATE_DEFAULT_TARGET_SCORE when unset. */
  targetScore?: number | null;
  now?: number;
}): { ok: true; mandate: SourcingMandate } | { ok: false; reason: string } => {
  const goal = params.goal.trim();
  const propertyIds = [...new Set(params.propertyIds.map((id) => id.trim()).filter(Boolean))];
  const ceiling = params.creditCeiling === null ? MANDATE_UNCAPPED_CREDITS : Math.floor(Number(params.creditCeiling));
  if (!goal) return { ok: false, reason: 'the goal is empty' };
  if (propertyIds.length === 0) return { ok: false, reason: 'no asset is named' };
  if (!Number.isFinite(ceiling) || ceiling < 1) return { ok: false, reason: 'the credit ceiling must be a positive number' };
  if (ceiling > MANDATE_MAX_CEILING_CREDITS && !isUncapped(ceiling)) return { ok: false, reason: `the credit ceiling is capped at ${MANDATE_MAX_CEILING_CREDITS}` };
  const targetScore = params.targetScore == null ? MANDATE_DEFAULT_TARGET_SCORE : Math.round(Number(params.targetScore));
  if (!Number.isFinite(targetScore) || targetScore < MANDATE_MIN_TARGET_SCORE || targetScore > 100) {
    return { ok: false, reason: `the target score must be between ${MANDATE_MIN_TARGET_SCORE} and 100` };
  }
  const now = params.now ?? Date.now();
  return {
    ok: true,
    mandate: { goal, propertyIds, creditCeiling: ceiling, committedCredits: 0, createdAt: new Date(now).toISOString(), expiresAt: new Date(now + MANDATE_TTL_MS).toISOString(), origin: params.origin ?? 'chat', unitId: params.unitId?.trim() || null, targetScore },
  };
};

/** May this mandate pay for a step of `credits` on `propertyId`? Says why not, for the refusal. */
export const mandateAllows = (
  mandate: SourcingMandate | null | undefined,
  step: { propertyId: string | null; credits: number },
  now = Date.now(),
): { ok: true } | { ok: false; reason: string } => {
  if (!mandate) return { ok: false, reason: 'no mandate' };
  if (Date.parse(mandate.expiresAt) <= now) return { ok: false, reason: 'the mandate has expired' };
  if (!step.propertyId || !mandate.propertyIds.includes(step.propertyId)) return { ok: false, reason: 'this asset is outside the mandate' };
  if (!Number.isFinite(step.credits) || step.credits <= 0) return { ok: false, reason: 'the step has no budget' };
  const left = mandate.creditCeiling - mandate.committedCredits;
  if (step.credits > left) return { ok: false, reason: `the step needs ${Math.ceil(step.credits)} credits and ${Math.max(0, Math.floor(left))} are left in the mandate` };
  return { ok: true };
};

export const commitToMandate = (mandate: SourcingMandate, credits: number): SourcingMandate => ({
  ...mandate,
  committedCredits: mandate.committedCredits + Math.ceil(credits),
});

/**
 * The mandate as the agent reads it on every turn. It lives in the browser, so without this line
 * the turn that wakes the agent when a run lands had no idea it was driving: it reported the run
 * and stopped, 48 credits of reserve unused (2026-09-28).
 */
export const describeMandate = (mandate: SourcingMandate | null | undefined, now = Date.now()): string => {
  if (!mandate || Date.parse(mandate.expiresAt) <= now) return '';
  const left = Math.max(0, mandate.creditCeiling - mandate.committedCredits);
  const target = mandate.targetScore ?? MANDATE_DEFAULT_TARGET_SCORE;
  return [
    'ACTIVE SOURCING MANDATE (you are driving Source for the user):',
    `- Goal, in their words: ${mandate.goal}`,
    `- Assets: ${mandate.propertyIds.join(', ')}`,
    ...(mandate.unitId
      ? [`- A UNIT FIT, for unit ${mandate.unitId} of that asset: read it with DIAGNOSE_ASSET_FOR_SOURCING unitId, draft the hypothesis from its brief, and launch with unitId and unitHypothesis. Never an asset-wide run under this mandate.`]
      : []),
    `- TARGET: a champion scoring ${target} or more that is no misfit for the asset. You do not stop before it, unless what is left cannot pay the next step or the user stops you.`,
    // The price in brands, because credits alone misled it: a "small first batch" of a quarter of
    // 25 credits bought ONE brand (2026-09-28).
    isUncapped(mandate.creditCeiling)
      ? `- No credit ceiling: the user wants the target reached, whatever it takes; committed ${mandate.committedCredits} so far; expires ${mandate.expiresAt}.`
      : `- Ceiling ${mandate.creditCeiling} credits, committed ${mandate.committedCredits}, left ${left} (about ${brandsForCredits(left, true)} more brands analysed with research); expires ${mandate.expiresAt}.`,
    ...(mandate.origin === 'launch-bar'
      ? ['- Recorded from the dashboard Launch bar: pressing Launch WAS the go. On the first turn, diagnose the asset, say the plan in two lines, configure the strategy and LAUNCH, all in this turn. Never ask "shall I go?".']
      : []),
    `- When a run has landed, do the work now, in this turn: read the shortlist (top score, the top ten), then either the champion is there (score ${target}+, no misfit) and you conclude with END_SOURCING_MANDATE and the named verdict, or you take the next step: EXTEND_SHORTLIST, correct the filters and relaunch, or REFINE_SHORTLIST. Announcing that you will examine it later is not a step.`,
    // Only a run landing wakes the agent: a turn that ends on a diagnosis alone parks the mandate
    // with credits left and nobody working (2026-09-28, 19 of 25 credits left idle).
    '- Every turn under this mandate ends with a run or extension started, or with END_SOURCING_MANDATE. Nothing wakes you except a run landing, so a turn that ends on an observation leaves the work stopped.',
  ].join('\n');
};

// THE SERVER DRIVER'S MONEY RULES (lib/leasing/mandateDriver.ts), pure so they are tested here.
export type MandateRow = { brand: string; score: number; status: string; rationale: string };

export type MandatePlan =
  | { kind: 'end'; outcome: 'expired' | 'champion' | 'ceiling'; best: MandateRow | null }
  | { kind: 'extend'; maxBrands: number; suggestedBrands: number; best: MandateRow | null };

/** What the landing of a pass calls for. Pure: the money rules live here and are tested. */
export const planNextStep = (mandate: ServerSourcingMandate, rows: MandateRow[], analysed: number, now = Date.now(), brandsLeft: number | null = null): MandatePlan => {
  const best = rows.filter((row) => row.status === 'SUCCESS').sort((a, b) => b.score - a.score)[0] ?? null;
  if (best && best.score >= mandate.targetScore) return { kind: 'end', outcome: 'champion', best };
  if (Date.parse(mandate.expiresAt) <= now) return { kind: 'end', outcome: 'expired', best };
  // Up to every eligible brand left: a fixed step made a no-ceiling mandate crawl 20 by 20 when
  // the evidence called for one wide sweep (owner, 2026-10-14).
  const affordable = isUncapped(mandate.creditCeiling)
    ? brandsLeft && brandsLeft > 0 ? brandsLeft : UNCAPPED_STEP_BRANDS
    : brandsForCredits(mandate.creditCeiling - mandate.committedCredits, true);
  const maxBrands = brandsLeft && brandsLeft > 0 ? Math.min(affordable, brandsLeft) : affordable;
  if (maxBrands < 1) return { kind: 'end', outcome: 'ceiling', best };
  // Widening steps: roughly double what has been read, at least five, never past the ceiling.
  return { kind: 'extend', maxBrands, suggestedBrands: Math.min(maxBrands, Math.max(5, analysed)), best };
};
